import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  NotificationState,
  NotificationTestResponse,
  PushSubscriptionResponse,
  type NotificationPreferences,
  type PushSubscriptionUpsert,
} from '@email/shared';
import { api } from './client.js';
import {
  settingsInvalidations,
  settingsKeys,
} from './query-cache.js';

export function useNotificationState(enabled = true) {
  return useQuery({
    queryKey: settingsKeys.notifications,
    queryFn: () =>
      api('/api/notifications', {
        schema: NotificationState,
      }),
    enabled,
  });
}

export function registerPushSubscription(value: PushSubscriptionUpsert) {
  return api('/api/notifications/subscription', {
    method: 'PUT',
    body: value,
    schema: PushSubscriptionResponse,
  });
}

export function useSaveNotificationPreferences() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (preferences: NotificationPreferences) =>
      api('/api/notifications/preferences', {
        method: 'PATCH',
        body: preferences,
        schema: NotificationState,
    }),
    onSuccess: (state) => {
      queryClient.setQueryData(settingsKeys.notifications, state);
    },
  });
}

export function useDeleteNotificationDevice() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      api(`/api/notifications/subscriptions/${id}`, { method: 'DELETE' }),
    onSettled: () => settingsInvalidations.notifications.invalidate(queryClient),
  });
}

export function useTestNotification() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (subscriptionId?: number) =>
      api('/api/notifications/test', {
        method: 'POST',
        body: subscriptionId === undefined ? {} : { subscriptionId },
        schema: NotificationTestResponse,
      }),
    onSettled: () => settingsInvalidations.notifications.invalidate(queryClient),
  });
}
